import React, { Component } from 'react'
import { forEach, result, escapeRegExp } from 'libraries/lodash'
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'
import colorhash from 'helpers/colorhash'
// pdfjs-dist calls the native Uint8Array.prototype.toHex/fromHex/setFromHex
// internally (e.g. for its own hash computations) - these are a very
// recently standardised proposal, and older/current-at-time-of-writing
// Chrome doesn't implement them yet, throwing "hashOriginal.toHex is not a
// function" from inside pdf.js's own code when that path is hit. babel's
// useBuiltIns: "usage" polyfill injection only analyses our own source for
// what to auto-polyfill, not pre-built third-party packages like this one,
// so it doesn't cover pdf.js's internal usage - loaded explicitly here,
// scoped to the module that actually needs it rather than the whole app.
//
// This only covers the *main thread* - pdf.js also does hashing work
// inside its own dedicated Worker (below), which is a separate JS realm
// with its own Uint8Array unaffected by these imports. That side is
// polyfilled separately in pdf.worker.entry.js.
import 'core-js/stable/typed-array/to-hex'
import 'core-js/stable/typed-array/from-hex'
import 'core-js/stable/typed-array/set-from-hex'
import * as pdfjsLib from 'pdfjs-dist'
import { PDFViewer, PDFFindController, EventBus, PDFLinkService } from 'pdfjs-dist/web/pdf_viewer.mjs'
import pdfViewerStyles from 'pdfjs-dist/web/pdf_viewer.css'
import findAndReplaceDOMText from 'findandreplacedomtext'

// Points at our own wrapper (pdf.worker.entry.js) rather than
// pdfjs-dist/build/pdf.worker.mjs directly, so the same toHex/fromHex/
// setFromHex polyfills above also get installed inside the worker's realm
// before pdf.js's own worker code runs - see that file for the full
// explanation of why this is needed separately from the main-thread import.
pdfjsLib.GlobalWorkerOptions.workerPort = new Worker(
  new URL('./pdf.worker.entry.js', import.meta.url),
  { type: 'module' }
)

const highlightOpacityHex = '4D'

export default class PdfViewer extends Component {
  state = {
    findBarQuery: '',
    findBar: true
  }

  container = null
  hasScrolled = false

  componentDidMount () {
    try {
      pdfViewerStyles.use()
      const container = document.getElementById('pdfViewer')
      this.eventBus = new EventBus()
      this.linkService = new PDFLinkService({ eventBus: this.eventBus })

      this.pdfFindController = new PDFFindController({
        linkService: this.linkService,
        eventBus: this.eventBus
      })

      this.pdfViewer = new PDFViewer({
        container,
        eventBus: this.eventBus,
        linkService: this.linkService,
        findController: this.pdfFindController
      })

      this.linkService.setViewer(this.pdfViewer)

      this.eventBus.on('textlayerrendered', this.handlePageRendered)
      this.eventBus.on('pagesinit', this.handlePagesInit)
      this.eventBus.on('updatefindcontrolstate', this.handleFindControlState)

      this.setPdf({ data: this.props.pdf })
      this.commandSubscription = this.props.$commands.subscribe(this.handleSearch)
    } catch (e) {
      console.error(e)
    }
  }

  componentWillUnmount () {
    try {
      pdfViewerStyles.unuse()
      clearInterval(this.interval)
      this.eventBus?.off('textlayerrendered', this.handlePageRendered)
      this.eventBus?.off('pagesinit', this.handlePagesInit)
      this.eventBus?.off('updatefindcontrolstate', this.handleFindControlState)
      this.commandSubscription.unsubscribe()
    } catch (e) {
      console.error(e)
    }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.textSearch !== this.props.textSearch) {
      this.handleSearch()
    }
    if (prevProps.currentScale !== this.props.currentScale) {
      this.pdfViewer.currentScaleValue = this.props.currentScale
    }
  }

  setPdf (pdf) {
    if (pdf && pdf.data) {
      pdfjsLib.getDocument({ data: pdf.data }).promise.then(pdfDocument => {
        this.linkService.setDocument(pdfDocument)
        this.pdfViewer.setDocument(pdfDocument)
      })
    }
  }

  revealParam = (node) => {
    if (!node || node.className !== 'highlight') {
      return
    }

    this.hasScrolled = this.scrollToElement(node)
  }

  handlePageRendered = (evt) => {
    const index = evt.pageNumber
    this.updateMarks(evt.source.div, index, this.props.parameters)
  }

  handlePagesInit = () => {
    this.pdfViewer.currentScaleValue = 'auto'
    const currentScale = this.pdfViewer.currentScale
    const { setMinScale, setCurrentScale } = this.props
    setMinScale(currentScale)
    setCurrentScale(currentScale)
    if (this.scrollToParamPage !== null) {
      this.scrollToElement(this.getPageElement(this.scrollToParamPage))
    }
  }

  handleFindControlState = () => {
    const element = document.querySelector('.textLayer .highlight.selected')
    if (element) {
      scrollIntoViewIfNeeded(element, true)
    }
  }

  get scrollToParam () {
    return result(this.props.parameters, this.props.scrollToParam, null)
  }

  get scrollToParamPage () {
    const param = this.scrollToParam
    const path = this.props.optionalPath?.substring(1) // remove leading .

    if (param && path) {
      if (param.type === 'MultiSelectParameterValue') { // handle getting page number for MultiSelectParameterValue
        return result(param, `${path}.descriptor.context.pageNumber`)
      }
      return result(param, [path, 'descriptor', 'context', 'pageNumber'])
    }

    if (param) {
      return result(param, 'descriptor.context.pageNumber') ||
        result(param, 'values[0].descriptor.context.pageNumber') ||
        1
    }

    return null
  }

  getPageElement (pageNumber) {
    return document.querySelector(`#pdfViewer .page[data-page-number="${pageNumber}"]`)
  }

  handleSearch = (findNext = true) => {
    const { textSearch: query } = this.props
    const findPrevious = !findNext
    if (query !== this.state.findBarPrevQuery) {
      this.eventBus.dispatch('find', {
        source: this,
        type: '',
        query,
        findPrevious,
        caseSensitive: false,
        highlightAll: true,
        entireWord: false
      })
      this.setState({ findBarPrevQuery: query })
    } else {
      this.eventBus.dispatch('find', {
        source: this,
        type: 'again',
        query,
        findPrevious
      })
    }
  }

  updateMarks (node, pageNumber, params) {
    if (this.scrollToParam !== null) {
      this.revealParam(
        this.updateMarksForParam(
          node,
          pageNumber
        )(
          this.scrollToParam,
          this.props.scrollToParam
        )
      )
    } else {
      forEach(
        params,
        (param, name) =>
          this.updateMarksForParam(node, pageNumber)(param, name)
      )
    }
  }

  updateMarksForParam = (node, pageNumber) => (param, name) => {
    if (!param) {
      return
    }
    const updateMarksForParam = this.updateMarksForParam(node, pageNumber)
    updateMarksForParam(param.minValue, name + '.minValue')
    updateMarksForParam(param.maxValue, name + '.maxValue')

    forEach(
      param.values || [],
      (param, i) =>
        updateMarksForParam(
          param,
          name + `.values[${i}]`
        )
    )

    const paramMark = param.mark || param.descriptor.context
    if (!paramMark) {
      return
    }

    let value = paramMark
    if (typeof paramMark !== 'string') {
      if (paramMark.pageNumber !== pageNumber) {
        return
      }
      value = paramMark.value
    }

    const regex = new RegExp(
      escapeRegExp(value).replace(/\s+/g, '\\s*'),
      'g'
    )
    const matches = []
    const group = result(param, 'config.group', '')
    const title = result(param, 'config.description', '')
    const color = this.scrollToParam ? 'rgba(255, 0, 0, 0.3)' : param.color || colorhash(param.descriptor.name, highlightOpacityHex)
    findAndReplaceDOMText(
      node,
      {
        find: regex,
        replace: (portion, match) => {
          if (match.index === paramMark.pageOccurrence) {
            const span = document.createElement('span')
            span.innerHTML = portion.text
            span.className = 'highlight'
            span.title = `${group}: ${title}`
            span.style.backgroundColor = color
            matches.push(span)
            return span
          } else {
            return portion.text
          }
        }
      }
    )

    return matches[0] || node
  }

  scrollToElement (pageElement) { // element is the page container
    if (!pageElement || this.hasScrolled) {
      return false
    }
    // scroll to the page
    scrollIntoViewIfNeeded(pageElement, true)
    // wait for the page content to load
    return this.waitForHighLightedElement(pageElement).then((highlightedElement) => {
      // found highlighted element and scrolling it to the center of the view
      if (highlightedElement) {
        scrollIntoViewIfNeeded(highlightedElement, {
          centerIfNeeded: true
        })
      }
    }).finally(() => {
      // no highlighted text found just the page scroll
      return true
    })
  }

  waitForHighLightedElement (targetNode = document, stopAfter = 3000) {
    const selector = 'span.highlight'
    return new Promise(resolve => {
      setTimeout(() => { // stop looking for the element after the provide time
        resolve(null)
        observer.disconnect()
      }, stopAfter)

      if (targetNode.querySelector(selector)) {
        return resolve(targetNode.querySelector(selector))
      }

      const observer = new MutationObserver(mutations => {
        if (targetNode.querySelector(selector)) {
          resolve(targetNode.querySelector(selector))
          observer.disconnect()
        }
      })
      observer.observe(targetNode, {
        childList: true,
        subtree: true
      })
    })
  }

  render () {
    return (
      <div className='pdf-viewer-wrapper relative o-hidden w-100 mhauto'>
        <div id='pdfViewer' className='absolute top0 left0 right0 bottom0 o-auto'>
          <div className='pdfViewer' />
        </div>
      </div>
    )
  }
}
